import React, { useEffect, useState } from 'react';
import { Product } from '../types';
import { displayPrice } from '../constants';
import { X, Plus, Minus, Ruler, Scale, Shield, Zap } from 'lucide-react';

interface ProductDetailModalProps {
  products: Product[];
  isOpen: boolean;
  onClose: () => void;
  isVerified: boolean;
  onLoginClick?: () => void;
  quantities: { [key: string]: number };
  onIncrement: (product: Product) => void;
  onDecrement: (productId: string) => void;
  modelNumber?: string;
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  products,
  isOpen,
  onClose,
  isVerified,
  quantities,
  onIncrement,
  onDecrement,
  modelNumber,
}) => {
  const [selectedVariant, setSelectedVariant] = useState(0);

  // Reset variant index when modal opens with a new product family
  useEffect(() => {
    if (isOpen) setSelectedVariant(0);
  }, [isOpen, products]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen || products.length === 0) return null;

  const selectedProduct = products[selectedVariant];
  const shownPrice = displayPrice(selectedProduct, isVerified);
  const currentQty = quantities[selectedProduct.id] || 0;
  const isMaxStock = currentQty >= selectedProduct.stock;
  const totalFamilyQty = products.reduce((sum, p) => sum + (quantities[p.id] || 0), 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-forge-deep/70 backdrop-blur-sm transition-opacity duration-300 ease-editorial"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4 lg:p-8">
        <div className="relative bg-paper-bright shadow-float max-w-5xl w-full max-h-[92vh] overflow-hidden animate-in fade-in zoom-in-95 duration-300 ease-editorial">

          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center bg-paper-bright/90 hover:bg-paper text-ink-700 hover:text-ink-950 border border-ink-200 transition-colors duration-150 ease-editorial"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2">

            {/* Image */}
            <div className="bg-paper p-10 lg:p-12 flex items-center justify-center relative aspect-square lg:aspect-auto lg:min-h-[600px] border-b lg:border-b-0 lg:border-r border-ink-200">

              {/* Category */}
              <span className="absolute top-5 left-5 font-mono text-[10px] tracking-micro uppercase text-ink-500">
                {selectedProduct.category}
              </span>

              {/* SKU */}
              <span className="absolute top-5 right-16 font-mono text-[10px] tracking-micro uppercase text-ink-500">
                {selectedProduct.sku}
              </span>

              {/* Total family quantity */}
              {totalFamilyQty > 0 && (
                <span className="absolute bottom-5 left-5 inline-flex items-center gap-2 bg-ember text-paper-warm px-3 py-1.5 font-mono text-[11px] tracking-wider">
                  {totalFamilyQty} in cart
                </span>
              )}

              <img
                src={selectedProduct.image}
                alt={selectedProduct.name}
                className="max-w-full max-h-[440px] object-contain mix-blend-multiply transition-opacity duration-300 ease-editorial"
              />
            </div>

            {/* Details */}
            <div className="p-8 lg:p-12 overflow-y-auto max-h-[60vh] lg:max-h-[92vh]">

              {/* Header */}
              <div className="mb-8 pb-6 border-b border-ink-200">
                <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-3">
                  — {selectedProduct.category} series
                </p>
                <h2 className="font-editorial italic font-light text-4xl text-ink-950 tracking-tight leading-tight">
                  {modelNumber ? `Incolma ${modelNumber}` : selectedProduct.category}.
                </h2>
              </div>

              {/* Buy box — price, size, and CTA visible without scrolling */}
              <div className="mb-8">
                <div className="flex items-end justify-between mb-5">
                  <div>
                    <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">
                      {isVerified ? 'Wholesale price' : 'Price'}
                    </p>
                    <p className="font-editorial italic text-4xl text-ink-950 leading-none">
                      ${shownPrice.toFixed(2)}
                      <span className="font-sans not-italic font-normal text-sm text-ink-500 ml-3">
                        CAD {isVerified ? '/ unit' : 'each'}
                      </span>
                    </p>
                  </div>
                  {selectedProduct.msrp > shownPrice && (
                    <div className="text-right">
                      <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">MSRP</p>
                      <p className="text-base text-ink-500 line-through">${selectedProduct.msrp.toFixed(2)}</p>
                    </div>
                  )}
                </div>

                {/* Size variants */}
                {products.length > 1 && (
                  <div className="mb-5">
                    <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-3">
                      — Select size
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {products.map((variant, index) => {
                        const variantQty = quantities[variant.id] || 0;
                        const isSelected = selectedVariant === index;
                        return (
                          <button
                            key={variant.id}
                            onClick={() => setSelectedVariant(index)}
                            className={`relative px-5 py-3 text-sm font-medium border transition-colors duration-150 ease-editorial ${
                              isSelected
                                ? 'border-ember bg-ember text-paper-warm'
                                : 'border-ink-200 bg-paper-bright text-ink-900 hover:border-ink-300'
                            }`}
                          >
                            {variant.bladeLength}"
                            {variantQty > 0 && (
                              <span className={`absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 text-[10px] font-mono rounded-full flex items-center justify-center ${
                                isSelected ? 'bg-paper-warm text-ember' : 'bg-ember text-paper-warm'
                              }`}>
                                {variantQty}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quantity / Add */}
                {currentQty === 0 ? (
                  <button
                    onClick={() => onIncrement(selectedProduct)}
                    disabled={isMaxStock}
                    className="w-full py-4 bg-ember hover:bg-ember-deep disabled:opacity-50 disabled:cursor-not-allowed text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                    {isVerified ? 'Add to order' : 'Add to cart'}
                  </button>
                ) : (
                  <div className="flex items-stretch border border-ink-200">
                    <button
                      onClick={() => onDecrement(selectedProduct.id)}
                      className="w-14 flex items-center justify-center text-ink-700 hover:bg-paper transition-colors duration-150"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <div className="flex-1 flex items-center justify-center font-editorial italic text-2xl text-ink-950 bg-paper-bright py-3.5">
                      {currentQty}
                    </div>
                    <button
                      onClick={() => onIncrement(selectedProduct)}
                      disabled={isMaxStock}
                      className="w-14 flex items-center justify-center text-ink-700 hover:bg-paper disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150"
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                )}

                {/* Stock + assurance */}
                <div className="flex items-center justify-between mt-4">
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">
                    {selectedProduct.stock} units available
                  </p>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">
                    18+ · ID on delivery
                  </p>
                </div>
              </div>

              {/* Description */}
              <p className="text-ink-700 text-base leading-[1.65] mb-8 pt-6 border-t border-ink-200">
                {selectedProduct.description}
              </p>

              {/* Specs */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-5 mb-8 p-6 bg-paper border border-ink-200">
                <div className="flex items-center gap-3">
                  <Ruler className="w-4 h-4 text-ink-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">Blade length</p>
                    <p className="font-editorial italic text-lg text-ink-950 leading-none mt-1">{selectedProduct.bladeLength}"</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Scale className="w-4 h-4 text-ink-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">Weight</p>
                    <p className="font-editorial italic text-lg text-ink-950 leading-none mt-1">{selectedProduct.weight} lbs</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-ink-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">Steel</p>
                    <p className="font-editorial italic text-base text-ink-950 leading-tight mt-1">{selectedProduct.specs.steel}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Zap className="w-4 h-4 text-ink-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">Hardness</p>
                    <p className="font-editorial italic text-lg text-ink-950 leading-none mt-1">{selectedProduct.specs.hardness}</p>
                  </div>
                </div>
              </div>

              {/* Performance */}
              <div className="mb-8">
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-4">
                  — Performance
                </p>
                <div className="space-y-3.5">
                  {[
                    { label: 'Toughness', value: selectedProduct.specs.toughness },
                    { label: 'Edge retention', value: selectedProduct.specs.retention },
                    { label: 'Flexibility', value: selectedProduct.specs.flexibility },
                    { label: 'Sharpness', value: selectedProduct.specs.sharpness },
                  ].map((stat) => (
                    <div key={stat.label} className="flex items-center gap-4">
                      <span className="text-sm text-ink-700 w-28">{stat.label}</span>
                      <div className="flex-1 h-[2px] bg-ink-200 overflow-hidden">
                        <div
                          className="h-full bg-ember transition-all duration-700 ease-editorial"
                          style={{ width: `${stat.value}%` }}
                        />
                      </div>
                      <span className="font-mono text-xs text-ink-500 w-8 text-right">{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Features */}
              {selectedProduct.pitch?.length > 0 && (
                <div className="mb-4">
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-4">
                    — Key features
                  </p>
                  <ul className="space-y-2.5">
                    {selectedProduct.pitch.map((feature, i) => (
                      <li key={i} className="border-l-2 border-ember/50 pl-4 text-sm text-ink-700 leading-relaxed">
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
